import { Card, Flex, Text, Button, Badge } from '@radix-ui/themes';
import { Pencil1Icon, TrashIcon, PlayIcon } from '@radix-ui/react-icons';

type IntegrationType = 'discord' | 'slack' | 'webhook';
type ConnectionStatus =
  | 'connected'
  | 'warning'
  | 'disconnected'
  | 'unconfigured';

interface IntegrationMetadata {
  channelCount?: number;
  webhookType?: string;
  httpMethod?: string;
}

interface IntegrationCardProps {
  type: IntegrationType;
  name: string;
  status?: ConnectionStatus;
  metadata?: IntegrationMetadata;
  onClick?: () => void;
  onEdit?: () => void;
  onTest?: () => void;
  onDelete: () => void;
}

const statusConfig: Record<ConnectionStatus, { color: string; label: string }> =
  {
    connected: { color: '#10b981', label: 'Connected' },
    warning: { color: '#f59e0b', label: 'Warning' },
    disconnected: { color: '#ef4444', label: 'Disconnected' },
    unconfigured: { color: '#6b7280', label: 'Unconfigured' },
  };

// Slack logo SVG component
const SlackIcon = () => (
  <svg viewBox="0 0 54 54" width="28" height="28">
    <g fill="none">
      <path
        d="M19.712.133a5.381 5.381 0 0 0-5.376 5.387 5.381 5.381 0 0 0 5.376 5.386h5.376V5.52A5.381 5.381 0 0 0 19.712.133m0 14.365H5.376A5.381 5.381 0 0 0 0 19.884a5.381 5.381 0 0 0 5.376 5.387h14.336a5.381 5.381 0 0 0 5.376-5.387 5.381 5.381 0 0 0-5.376-5.386"
        fill="#36C5F0"
      />
      <path
        d="M53.76 19.884a5.381 5.381 0 0 0-5.376-5.386 5.381 5.381 0 0 0-5.376 5.386v5.387h5.376a5.381 5.381 0 0 0 5.376-5.387m-14.336 0V5.52A5.381 5.381 0 0 0 34.048.133a5.381 5.381 0 0 0-5.376 5.387v14.364a5.381 5.381 0 0 0 5.376 5.387 5.381 5.381 0 0 0 5.376-5.387"
        fill="#2EB67D"
      />
      <path
        d="M34.048 54a5.381 5.381 0 0 0 5.376-5.387 5.381 5.381 0 0 0-5.376-5.386h-5.376v5.386A5.381 5.381 0 0 0 34.048 54m0-14.365h14.336a5.381 5.381 0 0 0 5.376-5.386 5.381 5.381 0 0 0-5.376-5.387H34.048a5.381 5.381 0 0 0-5.376 5.387 5.381 5.381 0 0 0 5.376 5.386"
        fill="#ECB22E"
      />
      <path
        d="M0 34.249a5.381 5.381 0 0 0 5.376 5.386 5.381 5.381 0 0 0 5.376-5.386v-5.387H5.376A5.381 5.381 0 0 0 0 34.25m14.336 0v14.364a5.381 5.381 0 0 0 5.376 5.387 5.381 5.381 0 0 0 5.376-5.387V34.25a5.381 5.381 0 0 0-5.376-5.387 5.381 5.381 0 0 0-5.376 5.387"
        fill="#E01E5A"
      />
    </g>
  </svg>
);

const integrationConfig: Record<
  IntegrationType,
  { icon: string | (() => JSX.Element); brandColor: string }
> = {
  discord: { icon: '💬', brandColor: '#5865f2' },
  slack: { icon: SlackIcon, brandColor: '#4a154b' },
  webhook: { icon: '🔗', brandColor: '#6b7280' },
};

export default function IntegrationCard({
  type,
  name,
  status = 'connected',
  metadata = {},
  onClick,
  onEdit,
  onTest,
  onDelete,
}: IntegrationCardProps) {
  const config = integrationConfig[type];
  const statusInfo = statusConfig[status];
  const isClickable = !!onClick;
  const hasActions = onEdit || onTest;

  const handleCardClick = (e: React.MouseEvent) => {
    // Don't trigger card click if clicking on action buttons
    if ((e.target as HTMLElement).closest('button')) {
      return;
    }
    onClick?.();
  };

  return (
    <Card
      role="article"
      aria-label={`${type} integration: ${name}`}
      title={`${name} - ${type.charAt(0).toUpperCase() + type.slice(1)}`}
      onClick={isClickable ? handleCardClick : undefined}
      style={{
        position: 'relative',
        cursor: isClickable ? 'pointer' : 'default',
        transition: 'all 0.2s ease',
        borderRadius: '12px',
        border: '1px solid var(--gray-a5)',
      }}
      onMouseEnter={(e) => {
        e.currentTarget.style.boxShadow =
          '0 10px 25px -5px rgba(0, 0, 0, 0.1), 0 8px 10px -6px rgba(0, 0, 0, 0.1)';
        e.currentTarget.style.transform = 'translateY(-2px)';
        e.currentTarget.style.borderColor = 'var(--gray-a7)';
      }}
      onMouseLeave={(e) => {
        e.currentTarget.style.boxShadow = '';
        e.currentTarget.style.transform = '';
        e.currentTarget.style.borderColor = 'var(--gray-a5)';
      }}
    >
      <Flex align="center" gap="3" style={{ padding: '16px' }}>
        {/* Icon */}
        <div
          style={{
            fontSize: '28px',
            width: '48px',
            height: '48px',
            borderRadius: '12px',
            background: `linear-gradient(135deg, ${config.brandColor}22 0%, ${config.brandColor}33 100%)`,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            flexShrink: 0,
          }}
        >
          {typeof config.icon === 'function' ? config.icon() : config.icon}
        </div>

        {/* Name + Status */}
        <Flex direction="column" gap="1" style={{ minWidth: 0, flex: 1 }}>
          <Text size="3" weight="medium" style={{
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            whiteSpace: 'nowrap'
          }}>
            {name}
          </Text>
          <Flex align="center" gap="2">
            <div
              role="status"
              aria-label={`Status: ${statusInfo.label}`}
              style={{
                width: '8px',
                height: '8px',
                borderRadius: '50%',
                backgroundColor: statusInfo.color,
                flexShrink: 0,
              }}
            />
            <Text size="2" color="gray">{statusInfo.label}</Text>

            {metadata.channelCount !== undefined && (
              <>
                <Text size="2" color="gray">•</Text>
                <Text size="2" color="gray">
                  {metadata.channelCount}{' '}
                  {metadata.channelCount === 1 ? 'channel' : 'channels'}
                </Text>
              </>
            )}

            {metadata.webhookType && (
              <>
                <Text size="2" color="gray">•</Text>
                <Badge size="1" variant="soft">{metadata.webhookType}</Badge>
              </>
            )}

            {metadata.httpMethod && (
              <Badge size="1" variant="soft" color="gray">
                {metadata.httpMethod}
              </Badge>
            )}
          </Flex>
        </Flex>

        {/* Actions */}
        <Flex gap="1" align="center" style={{ flexShrink: 0 }}>
          {onEdit && (
            <Button
              size="1"
              variant="ghost"
              onClick={(e) => {
                e.stopPropagation();
                onEdit();
              }}
              title="Configure"
              aria-label={`Configure ${name}`}
            >
              <Pencil1Icon aria-hidden="true" />
            </Button>
          )}

          {onTest && (
            <Button
              size="1"
              variant="ghost"
              onClick={(e) => {
                e.stopPropagation();
                onTest();
              }}
              title="Test connection"
              aria-label={`Test connection for ${name}`}
            >
              <PlayIcon aria-hidden="true" />
            </Button>
          )}

          <Button
            size="1"
            variant="ghost"
            color="red"
            onClick={(e) => {
              e.stopPropagation();
              onDelete();
            }}
            title="Delete"
            aria-label={`Delete ${name}`}
            style={{ opacity: 0.6 }}
            onMouseEnter={(e) => {
              e.currentTarget.style.opacity = '1';
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.opacity = '0.6';
            }}
          >
            <TrashIcon aria-hidden="true" />
          </Button>
        </Flex>
      </Flex>
    </Card>
  );
}
